import { useMutation } from '@tanstack/react-query'
import {
  AccountDetailsDto,
  AccountManagementControllerService,
  ChangePasswordDto,
  ForgotPasswordRequest,
  LoginControllerService,
  OpenAPI,
  PasswordResetRequest,
  TestVerificationCodeRequestDto,
  UserMaintenanceControllerService,
  UserRegistrationControllerService,
} from '../../generated'
import { request } from '../../generated/core/request'
import { notification } from '../../genericComponents/Notification'
import { useRead } from '../apiUtils'
import { LoginFormDto } from '../../authentication/login/LoginPage'

export const useApiChangePassword = () =>
  useMutation({
    mutationFn: (dto: ChangePasswordDto) =>
      AccountManagementControllerService.updateRegAccountChangePassword({ requestBody: dto }),

    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Account details successfully saved')
    },
  })

export const useApiUpdateAccountDetails = () =>
  useMutation({
    mutationFn: (dto: AccountDetailsDto) =>
      AccountManagementControllerService.updateAccountDetails({ requestBody: dto }),
    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Account details successfully saved')
    },
  })
export const useApiForgotPassword = () =>
  useMutation({
    mutationFn: (dto: ForgotPasswordRequest) =>
      UserMaintenanceControllerService.forgotPassword({ requestBody: dto }),
    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Email sent!')
    },
  })

export const useApiVerifyOTP = (continuef: (code: string) => void, notify = true) => {
  return useMutation({
    mutationFn: (xOtpCode: string) => {
      return AccountManagementControllerService.getVerifyOtpCode({ xOtpCode }).then(() => xOtpCode)
    },
    onError: (error) => {
      notify && notification.error(error)
    },
    onSuccess: (data) => {
      continuef(data)
    },
  })
}

export const useApiUser = () =>
  useRead(AccountManagementControllerService.getAccountDetails, 'currentUser')

export const useApiChangeAccountDetails = () => {
  return useMutation({
    mutationFn: (requestBody: AccountDetailsDto) => {
      return UserRegistrationControllerService.changeAccountDetails({
        requestBody,
      })
    },
    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Account details changed')
    },
  })
}

export const useApiRegistrationState = () =>
  useRead(UserRegistrationControllerService.getRegAccountDetails, 'registrationState')

export const useApiCommitTfa = () => {
  return useMutation({
    mutationFn: (requestBody: TestVerificationCodeRequestDto) => {
      return UserRegistrationControllerService.commitTfa({
        requestBody,
      })
    },
    onError: (error) => {
      notification.error(error)
    },
  })
}

export const useApiCompleteRegistration = () => {
  return useMutation({
    mutationFn: () => {
      return UserRegistrationControllerService.completeRegistration()
    },
    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Registration complete')
    },
  })
}

export const useApiResetPassword = () => {
  return useMutation({
    mutationFn: async (requestBody: PasswordResetRequest) => {
      return request(OpenAPI, {
        method: 'POST',
        url: '/api/v1/maintenance/reset_password',
        body: requestBody,
        headers: {
          'X-token': requestBody.token as string,
        },
        mediaType: 'application/json',
      })
    },
    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Registration complete')
    },
  })
}

export const useApiEnableUser = () => {
  return useMutation({
    mutationFn: async (requestBody: PasswordResetRequest) => {
      return request(OpenAPI, {
        method: 'POST',
        url: '/api/v1/maintenance/enable-user',
        body: requestBody,
        headers: {
          'X-token': requestBody.token as string,
        },
        mediaType: 'application/json',
      })
    },
    onError: (error) => {
      notification.error(error)
    },
    onSuccess: () => {
      notification.success('Registration complete')
    },
  })
}

export const useApiVerifyEmail = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      return request(OpenAPI, {
        method: 'GET',
        url: `/api/v1/maintenance/verify_email?token=${token}`,
        headers: {
          'X-token': token,
        },
        mediaType: 'application/json',
      })
    },
  })
}

export const useApiLogin = () => {
  return useMutation({
    mutationFn: async (requestBody: LoginFormDto) => {
      return request(OpenAPI, {
        method: 'POST',
        url: '/login',
        formData: requestBody,
      })
    },
  })
}

export const useApiVerifyCode = () => {
  return useMutation({
    mutationFn: LoginControllerService.tfaVerifyLogin,
  })
}
